import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { ChevronDown, ExternalLink, FileDown } from 'lucide-react';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from './ui/accordion';
import { api, API } from '../lib/api';

// Privacy-safe menu click counter: anonymous aggregate totals only.
// No cookies, no identifiers, nothing about the visitor is sent or stored -
// just "this menu link was used". Never blocks navigation if it fails.
const trackMenuClick = (groupId, item) => {
  try {
    const payload = JSON.stringify({ group_id: groupId, item_id: item.id });
    if (navigator.sendBeacon) {
      navigator.sendBeacon(`${API}/nav-menu/click`, new Blob([payload], { type: 'application/json' }));
    } else {
      api.post('/nav-menu/click', { group_id: groupId, item_id: item.id }).catch(() => {});
    }
  } catch (e) { /* analytics must never break navigation */ }
};

// Grouped public navigation (desktop dropdowns + mobile accordions).
// The structure is fully editable in Admin → Menu Manager (/api/nav-menu).
// This fallback mirrors the backend defaults so the header still works if the
// menu API is briefly unavailable.
const FALLBACK_MENU = [
  { id: 'home', label: 'Home', url: '/home', show_desktop: true, show_mobile: true, items: [] },
  { id: 'request-a-sign', label: 'Request a Sign', url: '/request-a-sign', show_desktop: true, show_mobile: true, items: [] },
  { id: 'election-events', label: 'Election Event List', url: '/elections/events', show_desktop: true, show_mobile: true, items: [
    { id: 'all', label: 'All Debates & Candidate Events', url: '/elections/events' },
    { id: 'dates', label: 'Key Election Dates', url: '/elections/key-dates' },
    { id: 'add', label: 'Add Your Event', url: '/elections/submit-event' },
    { id: 'reminders', label: 'Weekly Reminder Emails', url: '/elections/reminders' },
    { id: 'how-to-vote', label: 'How to Vote', url: '/elections/how-to-vote' },
    { id: 'running', label: "Who's Running", url: '/whos-running' },
  ] },
  { id: 'pita', label: 'PITA', url: '/issue-tracker', show_desktop: true, show_mobile: true, items: [
    { id: 'overview', label: 'PITA Overview', url: '/issue-tracker#overview' },
    { id: 'report', label: 'Report a Ward 8 Issue', url: '/issue-tracker/report' },
    { id: 'track', label: 'Track My Issue', url: '/issue-tracker/track' },
    { id: 'map', label: 'Public Issue Map', url: '/map' },
    { id: 'reports', label: 'Ward 8 Reports', url: '/reports' },
    { id: 'call', label: 'Who Do I Call?', url: '/who-do-i-call' },
    { id: 'support', label: 'Support PITA', url: '/support-pita' },
  ] },
  { id: 'media', label: 'Media', url: '/media', show_desktop: true, show_mobile: true, items: [
    { id: 'h-videos', label: "Steve's Videos", url: '#', heading: true },
    { id: 'videos', label: "All of Steve's Videos", url: '/videos' },
    { id: 'two-minutes', label: 'Two Minutes with Steve', url: '/videos?category=two_minutes' },
    { id: 'events', label: 'Events', url: '/videos?category=election_events' },
    { id: 'other', label: 'Other', url: '/videos?category=other' },
    { id: 'h-posters', label: 'Posters', url: '#', heading: true },
    { id: 'paper-ballot-series', label: 'Paper Ballot Series', url: '/paper-ballots#series' },
    { id: 'h-photos', label: 'Photos', url: '#', heading: true },
    { id: 'ward8-photos', label: 'Beautiful Ward 8', url: '/gallery?album=ward8' },
  ] },
  { id: 'about', label: 'About', url: '/about', show_desktop: true, show_mobile: true, items: [
    { id: 'about', label: 'About Steve', url: '/about' },
    { id: 'platform', label: 'Platform', url: '/platform' },
    { id: 'cv-flyer', label: 'CV & Flyer', url: '/about/cv-flyer' },
    { id: 'reviews', label: 'Reviews', url: '/reviews' },
    { id: 'why', label: "Why I'm Running", url: '/start-here#why-im-running' },
    { id: 'tools', label: "Steve's Election Tools", url: '/about/election-tools' },
  ] },
  { id: 'resources', label: 'Resources', url: '/resources', show_desktop: true, show_mobile: true, items: [
    { id: 'all', label: 'All Resources', url: '/resources' },
    { id: 'running', label: "Who's Running", url: '/whos-running' },
    { id: 'find-ward', label: 'Find Your Ward', url: '/find-your-ward' },
    { id: 'boundaries', label: 'Ward Boundaries Map', url: '/elections/ward-boundaries' },
    { id: 'pita-map', label: 'PITA Issue Map', url: '/map' },
    { id: 'money', label: 'City Data & Finances', url: '/resources#money' },
    { id: 'council', label: 'Council & Accountability', url: '/resources#council' },
    { id: 'ward8', label: 'Ward 8 Information', url: '/ward-8' },
    { id: 'happening', label: "What's Happening", url: '/whats-happening' },
    { id: 'get-involved', label: 'Volunteer & Get Involved', url: '/get-involved' },
    { id: 'newsroom', label: 'Newsroom', url: '/newsroom' },
  ] },
  { id: 'contact', label: 'Contact', url: '/contact', show_desktop: true, show_mobile: true, items: [] },
  { id: 'donate', label: 'Donate', url: '/donate', show_desktop: true, show_mobile: true, items: [] },
];

export const useNavMenu = () => {
  const [groups, setGroups] = useState(FALLBACK_MENU);
  useEffect(() => {
    api.get('/nav-menu').then((r) => {
      if (r.data?.groups?.length) setGroups(r.data.groups);
    }).catch(() => {});
  }, []);
  return groups;
};

const itemPath = (url) => (url || '').split('#')[0].split('?')[0];

export const useIsItemActive = () => {
  const location = useLocation();
  return useCallback((item) => {
    if (!item || item.heading || item.external || item.download || (item.url || '').startsWith('http')) return false;
    const p = itemPath(item.url);
    if (!p) return false;
    return p === '/' ? location.pathname === '/' : location.pathname === p;
  }, [location.pathname]);
};

// One menu entry, shared by desktop dropdowns and the mobile accordion.
const MenuItemLink = ({ item, groupId, mobile, active, onNavigate, testId }) => {
  if (item.heading) {
    return (
      <p className={`px-3 pt-3 pb-1 text-[11px] font-black uppercase tracking-[0.18em] text-slate-400 ${mobile ? 'mt-1' : ''}`} data-testid={testId} role="presentation">{item.label}</p>
    );
  }
  const handleClick = () => {
    trackMenuClick(groupId, item);
    if (onNavigate) onNavigate();
  };
  const base = mobile
    ? `flex items-center gap-2 w-full min-h-[44px] px-3 py-3 rounded-lg text-[15px] font-semibold ${active ? 'bg-[#eaf2ff] text-[#1a2744]' : 'text-slate-700 hover:bg-slate-100 hover:text-[#0f172a]'}`
    : `flex items-center gap-2 w-full px-3 py-2.5 rounded-lg text-sm font-semibold ${active ? 'bg-[#eaf2ff] text-[#1a2744]' : 'text-[#1a2744] hover:bg-slate-100'}`;
  const label = (mobile && item.mobile_label) ? item.mobile_label : item.label;
  const inner = (
    <>
      {item.download && <FileDown className={`h-4 w-4 shrink-0 ${mobile ? 'text-[#24406f]' : 'text-[#24406f]'}`} />}
      <span className="flex-1 text-left leading-snug">{label}</span>
      {(item.external || item.new_tab) && !item.download && (
        <ExternalLink className={`h-3.5 w-3.5 shrink-0 ${mobile ? 'text-slate-400' : 'text-slate-400'}`} aria-hidden="true" />
      )}
    </>
  );
  if (item.download || item.external || (item.url || '').startsWith('http') || (item.url || '').startsWith('/api/')) {
    const newTab = item.new_tab && !item.download;
    return (
      <a href={item.url} className={base} data-testid={testId} onClick={handleClick}
         target={newTab ? '_blank' : undefined} rel={newTab ? 'noopener noreferrer' : undefined}
         aria-label={`${label}${newTab ? ' (opens in a new tab)' : ''}${item.download ? ' (download)' : ''}`}>
        {inner}
      </a>
    );
  }
  return (
    <Link to={item.url} className={base} data-testid={testId} onClick={handleClick}>
      {inner}
    </Link>
  );
};

// ---------------- Desktop dropdown navigation ----------------
export const DesktopNavMenus = ({ groups }) => {
  const [open, setOpen] = useState('');
  const location = useLocation();
  const navRef = useRef(null);
  const isActive = useIsItemActive();

  useEffect(() => { setOpen(''); }, [location.pathname, location.hash, location.key]);
  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') setOpen(''); };
    const onClick = (e) => { if (navRef.current && !navRef.current.contains(e.target)) setOpen(''); };
    document.addEventListener('keydown', onKey);
    document.addEventListener('click', onClick);
    return () => { document.removeEventListener('keydown', onKey); document.removeEventListener('click', onClick); };
  }, []);

  const desktopGroups = groups.filter((g) => g.show_desktop !== false);

  return (
    <nav ref={navRef} className="hidden lg:flex items-center gap-0.5" aria-label="Main navigation" data-testid="desktop-nav">
      {desktopGroups.map((g) => {
        const clickable = (g.items || []).filter((i) => !i.heading);
        const groupActive = clickable.some(isActive) || (g.url && location.pathname === itemPath(g.url));
        const opened = open === g.id;
        const btnClass = `flex items-center gap-1 px-2 xl:px-2.5 py-2 rounded-lg text-[12.5px] xl:text-[13.5px] font-bold whitespace-nowrap transition-colors duration-150 ${groupActive ? 'bg-[#eaf2ff] text-[#1a2744]' : opened ? 'bg-slate-100 text-[#0f172a]' : 'text-[#1a2744] hover:bg-slate-100 hover:text-[#0f172a]'}`;
        // Direct link (Home, Request a Sign, Contact, Donate): no dropdown at all.
        if (!clickable.length && g.url) {
          const donate = g.id === 'donate';
          return (
            <Link key={g.id} to={g.url} onClick={() => trackMenuClick(g.id, { id: 'direct' })}
              className={donate ? `ml-1 inline-flex items-center px-3 py-2 rounded-lg text-[12.5px] xl:text-[13.5px] font-black whitespace-nowrap bg-[#C9A84C] text-[#1a2744] hover:bg-[#b8973f] btn-press ${groupActive ? 'ring-2 ring-[#1a2744]/30' : ''}` : btnClass}
              aria-current={groupActive ? 'page' : undefined} data-testid={`desktop-nav-group-${g.id}`}>
              {g.desktop_label || g.label}
            </Link>
          );
        }
        return (
          <div key={g.id} className="relative" onMouseEnter={() => setOpen(g.id)} onMouseLeave={() => setOpen((o) => (o === g.id ? '' : o))}>
            <button
              onClick={() => setOpen(opened ? '' : g.id)}
              aria-expanded={opened}
              aria-haspopup="menu"
              className={btnClass}
              data-testid={`desktop-nav-group-${g.id}`}
            >
              {g.desktop_label || g.label}
              <ChevronDown className={`h-3.5 w-3.5 transition-transform duration-150 ${opened ? 'rotate-180' : ''}`} aria-hidden="true" />
            </button>
            {opened && (
              <div role="menu" className="absolute left-0 top-full pt-1.5 z-50" data-testid={`desktop-nav-panel-${g.id}`}>
                <div className="w-72 rounded-xl bg-white shadow-2xl border border-slate-200 p-1.5">
                  {g.url && (
                    <Link to={g.url} onClick={() => { trackMenuClick(g.id, { id: 'landing' }); setOpen(''); }}
                      className="flex items-center justify-between w-full px-3 py-2.5 rounded-lg text-sm font-black text-white bg-[#1a2744] hover:bg-[#24406f] mb-1"
                      data-testid={`desktop-nav-landing-${g.id}`}>
                      Open {g.desktop_label || g.label} <ChevronDown className="h-3.5 w-3.5 -rotate-90" aria-hidden="true" />
                    </Link>
                  )}
                  {(g.items || []).map((item) => (
                    <MenuItemLink key={item.id} item={item} groupId={g.id} active={isActive(item)}
                      onNavigate={() => setOpen('')}
                      testId={`desktop-nav-item-${g.id}-${item.id}`} />
                  ))}
                </div>
              </div>
            )}
          </div>
        );
      })}
    </nav>
  );
};

// ---------------- Mobile hamburger accordion navigation ----------------
export const MobileNavAccordion = ({ groups, onNavigate }) => {
  const location = useLocation();
  const isActive = useIsItemActive();
  const mobileGroups = groups.filter((g) => g.show_mobile !== false);
  // Open the section containing the current page so visitors see where they are
  const activeGroup = mobileGroups.find((g) => (g.items || []).some(isActive) || (g.url && location.pathname === itemPath(g.url)));

  return (
    <Accordion type="multiple" defaultValue={activeGroup ? [activeGroup.id] : []} className="w-full" data-testid="mobile-nav-drawer">
      {mobileGroups.map((g) => {
        const clickable = (g.items || []).filter((i) => !i.heading);
        const groupActive = clickable.some(isActive) || (g.url && location.pathname === itemPath(g.url));
        if (!clickable.length && g.url) {
          return (
            <div key={g.id} className="border-b border-slate-200">
              <Link to={g.url} onClick={() => { trackMenuClick(g.id, { id: 'direct' }); if (onNavigate) onNavigate(); }}
                className={`flex items-center px-4 py-3.5 min-h-[48px] text-[15px] font-bold hover:bg-slate-50 ${groupActive ? 'text-[#1a2744] bg-[#eaf2ff]' : g.id === 'donate' ? 'text-[#7a5c1c]' : 'text-slate-800'}`}
                aria-current={groupActive ? 'page' : undefined} data-testid={`mobile-nav-group-${g.id}`}>
                {g.mobile_label || g.label}
              </Link>
            </div>
          );
        }
        return (
        <AccordionItem key={g.id} value={g.id} className="border-slate-200">
          <AccordionTrigger
            className={`px-4 py-3.5 min-h-[48px] text-[15px] font-bold hover:no-underline hover:bg-slate-50 rounded-none [&>svg]:text-[#b91c1c] ${groupActive ? 'text-[#1a2744]' : 'text-slate-800'}`}
            data-testid={`mobile-nav-group-${g.id}`}
          >
            {g.mobile_label || g.label}
          </AccordionTrigger>
          <AccordionContent className="pb-2">
            <div className="px-2 space-y-0.5">
              {g.url && (
                <Link to={g.url} onClick={() => { trackMenuClick(g.id, { id: 'landing' }); if (onNavigate) onNavigate(); }}
                  className="flex items-center w-full min-h-[44px] px-3 py-3 rounded-lg text-[15px] font-black text-white bg-[#1a2744]"
                  data-testid={`mobile-nav-landing-${g.id}`}>
                  Open {g.mobile_label || g.label}
                </Link>
              )}
              {(g.items || []).map((item) => (
                <MenuItemLink key={item.id} item={item} groupId={g.id} mobile active={isActive(item)}
                  onNavigate={onNavigate}
                  testId={`mobile-nav-item-${g.id}-${item.id}`} />
              ))}
            </div>
          </AccordionContent>
        </AccordionItem>
        );
      })}
    </Accordion>
  );
};
